import { Button } from './ui/button';
import { Moon, Sun, ArrowRight, Menu, X } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import aiLogo from '../assets/ai_logo_light_transparent.png';

export default function Navbar() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we're on a blog page
  const isBlogPage = location.pathname.startsWith('/blog');

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(newTheme);
    localStorage.setItem('theme', newTheme);
    setTheme(newTheme);
  };

  const scrollToTop = () => {
    if (isBlogPage) {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToCourses = () => {
    if (isBlogPage) {
      navigate('/#courses');
    } else {
      document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToSection = (sectionId: string) => {
    if (isBlogPage) {
      navigate(`/#${sectionId}`);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleMobileNavigation = (action: () => void) => {
    action();
    setIsMobileMenuOpen(false);
  };



  return (
    <nav className="frosted-glass-navbar">
      <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img
                src={aiLogo}
                alt="AI Kurzy Logo"
                className="h-8 w-auto"
              />
              <a onClick={scrollToTop} className="text-xl font-bold text-foreground cursor-pointer">
                Kurzy o AI pre všetkých
              </a>
            </div>

            <div className="flex items-center gap-4">
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-6">
                <a
                  onClick={scrollToTop}
                  className="text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Domov
                </a>
                <a
                  onClick={scrollToCourses}
                  className="text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Kurzy
                </a>
                <a
                  onClick={() => navigateToSection('schedule')}
                  className="text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Termíny
                </a>
                <a
                  onClick={() => navigateToSection('contact')}
                  className="text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Kontakt
                </a>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 text-yellow-500" />
                ) : (
                  <Moon className="h-5 w-5 text-blue-500" />
                )}
              </Button>

              <Link to="/blog" className="hidden md:flex items-center">
                <Button
                  variant="ghost"
                  className="relative group px-3 py-1 h-auto rounded-md
                            border border-primary/20
                            dark:text-white text-foreground
                            hover:bg-primary/10 dark:hover:bg-primary/20
                            transition-all duration-200
                            blog-button-v2"
                >
                  Blog
                  <ArrowRight className="ml-1 h-3.5 w-3.5 opacity-70
                                      group-hover:translate-x-1
                                      transition-transform duration-200 ease-out" />
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden rounded-full"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-md">
              <div className="px-4 py-4 space-y-3">
                <a
                  onClick={() => handleMobileNavigation(scrollToTop)}
                  className="block text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer py-2"
                >
                  Domov
                </a>
                <a
                  onClick={() => handleMobileNavigation(scrollToCourses)}
                  className="block text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer py-2"
                >
                  Kurzy
                </a>
                <a
                  onClick={() => handleMobileNavigation(() => navigateToSection('schedule'))}
                  className="block text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer py-2"
                >
                  Termíny
                </a>
                <a
                  onClick={() => handleMobileNavigation(() => navigateToSection('contact'))}
                  className="block text-muted-foreground hover:text-foreground dark:text-gray-200 dark:hover:text-white transition-colors cursor-pointer py-2"
                >
                  Kontakt
                </a>
                <Link
                  to="/blog"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2"
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start relative group px-3 py-2 h-auto rounded-md
                              border border-primary/20
                              dark:text-white text-foreground
                              hover:bg-primary/10 dark:hover:bg-primary/20
                              transition-all duration-200"
                  >
                    Blog
                    <ArrowRight className="ml-1 h-3.5 w-3.5 opacity-70
                                        group-hover:translate-x-1
                                        transition-transform duration-200 ease-out" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
      </div>
    </nav>
  );
}